import { MONSTER_ARCHETYPES, type MonsterArchetype } from '@d2/core';
import Phaser from 'phaser';

import { ACTION_MARKS, type ActionMark } from './action-art';
import {
  CAVE_TILESET,
  EDGE_WIDTH,
  ENTITY_OTHER_TEXTURE,
  ENTITY_SIZE,
  ENTITY_TEXTURE,
  FLOOR_TILE,
  GROUND_ITEM_SIZE,
  GROUND_ITEM_TEXTURE,
  MONSTER_BAR,
  MONSTER_BAR_FILL,
  MONSTER_BAR_FILL_TEXTURE,
  MONSTER_BAR_TEXTURE,
  MONSTER_CROWN,
  MONSTER_FRAME,
  MONSTER_HEAD_RISE,
  MONSTER_RING,
  MONSTER_SIZE,
  PALETTE,
  ROCK_RISE,
  ROCK_TILE,
  STAIRS_BEACON_SIZE,
  STAIRS_BEACON_TEXTURE,
  STAIRS_SIZE,
  STAIRS_STEPS,
  STAIRS_TEXTURE,
  TILESET_IMAGE,
  TILE_FRAME,
  TILE_SPRITES,
  frameOrigin,
} from './art-spec';
import { ISO_TILE } from './iso';
import { MONSTER_BADGES, type MonsterLook, monsterLook } from './monster-art';

/**
 * Every pixel the renderer draws, generated at runtime from primitives.
 *
 * Deliberate, not a shortcut: real art is post-M8 (TODO row 9), and shipping
 * placeholder PNGs now would mean an asset pipeline, a licence question and a
 * loader, all of which would then be redone. A `Graphics` object baked into a
 * texture costs none of that and keeps the repo asset-free.
 *
 * The shapes exist to make *depth* legible, which is what this milestone has to
 * prove: a flat diamond for floor, a raised block for rock, a capsule for
 * something that stands between the two.
 */

/** Phaser's polygon calls take its own vector type, so corners are built as one. */
const corner = (x: number, y: number): Phaser.Math.Vector2 => new Phaser.Math.Vector2(x, y);

/** The four corners of a tile diamond whose bounding box starts at `(left, top)`. */
const diamond = (left: number, top: number): readonly Phaser.Math.Vector2[] => [
  corner(left + ISO_TILE.width / 2, top),
  corner(left + ISO_TILE.width, top + ISO_TILE.height / 2),
  corner(left + ISO_TILE.width / 2, top + ISO_TILE.height),
  corner(left, top + ISO_TILE.height / 2),
];

/** One lit side of a raised block: an edge of the top face, dropped by `ROCK_RISE`. */
const side = (from: Phaser.Math.Vector2, to: Phaser.Math.Vector2): readonly Phaser.Math.Vector2[] => [
  from,
  to,
  corner(to.x, to.y + ROCK_RISE),
  corner(from.x, from.y + ROCK_RISE),
];

const fillOutlined = (
  graphics: Phaser.GameObjects.Graphics,
  points: readonly Phaser.Math.Vector2[],
  colour: number,
  edge: number,
): void => {
  graphics.fillStyle(colour, 1);
  graphics.fillPoints([...points], true);
  graphics.lineStyle(EDGE_WIDTH, edge, 1);
  graphics.strokePoints([...points], true);
};

/**
 * The two-frame tileset: floor on the left, rock on the right, a gutter between
 * them.
 *
 * A tileset has exactly one frame size, so the flat floor diamond simply sits in
 * the bottom band of its frame — where a rock's base is — and the tile offset
 * applied when the tileset is registered lines both up with the grid.
 */
const drawTileset = (graphics: Phaser.GameObjects.Graphics): void => {
  const floorFrame = frameOrigin(0);
  const rockFrame = frameOrigin(1);

  fillOutlined(graphics, diamond(floorFrame.x, floorFrame.y + ROCK_RISE), PALETTE.floor, PALETTE.floorEdge);

  const [north, east, south, west] = diamond(rockFrame.x, rockFrame.y);

  // Four corners, always — a diamond that lost one is a programming error.
  if (north === undefined || east === undefined || south === undefined || west === undefined) {
    throw new Error('A tile diamond must have four corners');
  }

  fillOutlined(graphics, side(west, south), PALETTE.rockLeft, PALETTE.rockEdge);
  fillOutlined(graphics, side(south, east), PALETTE.rockRight, PALETTE.rockEdge);
  fillOutlined(graphics, [north, east, south, west], PALETTE.rockTop, PALETTE.rockEdge);
};

/**
 * One capsule, in the colours it is asked for.
 *
 * Two of them are baked: this client's entity and everybody else's. A colour
 * apart rather than a shape apart, because two capsules that overlap have to be
 * told apart by the pixel underneath them — which is the only way
 * entity-against-entity depth can be proven at all.
 */
const drawEntity = (graphics: Phaser.GameObjects.Graphics, fill: number, edge: number): void => {
  const inset = EDGE_WIDTH;
  const width = ENTITY_SIZE.width - inset * 2;
  const height = ENTITY_SIZE.height - inset * 2;

  graphics.fillStyle(fill, 1);
  graphics.fillRoundedRect(inset, inset, width, height, width / 2);
  graphics.lineStyle(EDGE_WIDTH * 2, edge, 1);
  graphics.strokeRoundedRect(inset, inset, width, height, width / 2);
};

/**
 * One monster, in one frame: the ring on the ground, the body standing on it,
 * and the crown for the one body in a pack that has earned it.
 *
 * **Shape *and* colour carry the archetype**, not colour alone. A player has to
 * read "that one shoots" while the body is half behind a rock, and a silhouette
 * survives that better than a hue does — so a melee body is a broad capsule, a
 * ranged one is a narrow body with a bow held across it, and a caster is a
 * pointed robe. All three are solid through their middle, which is also what
 * lets one pixel probe name the archetype whatever the standing.
 *
 * Drawn back to front, exactly as the eye expects: the ring lies on the floor
 * under the feet, the body stands over it, the crown floats above the head.
 */
const drawMonster = (graphics: Phaser.GameObjects.Graphics, look: MonsterLook): void => {
  const middle = MONSTER_FRAME.width / 2;
  const feet = MONSTER_HEAD_RISE;
  const head = feet - MONSTER_SIZE.height;

  if (look.ring !== null) {
    // Fill only. An outline here would sit exactly where a probe asks whether
    // this body is elite, and a dark rim would answer for the ring.
    graphics.fillStyle(look.ring, 1);
    graphics.fillEllipse(middle, feet, MONSTER_RING.width, MONSTER_RING.height);
  }

  graphics.fillStyle(look.fill, 1);
  graphics.lineStyle(EDGE_WIDTH, PALETTE.monsterEdge, 1);

  if (look.archetype === 'melee') {
    const width = MONSTER_SIZE.width;

    graphics.fillRoundedRect(middle - width / 2, head, width, MONSTER_SIZE.height, width / 2);
    graphics.strokeRoundedRect(middle - width / 2, head, width, MONSTER_SIZE.height, width / 2);
  } else if (look.archetype === 'ranged') {
    const width = MONSTER_SIZE.width - 10;
    const bow = head + MONSTER_SIZE.height / 3;

    graphics.fillRoundedRect(middle - width / 2, head, width, MONSTER_SIZE.height, width / 2);
    graphics.strokeRoundedRect(middle - width / 2, head, width, MONSTER_SIZE.height, width / 2);
    graphics.fillRect(middle - MONSTER_SIZE.width / 2 - 4, bow, MONSTER_SIZE.width + 8, EDGE_WIDTH * 4);
  } else {
    const points = [
      new Phaser.Math.Vector2(middle, head),
      new Phaser.Math.Vector2(middle + MONSTER_SIZE.width / 2, feet),
      new Phaser.Math.Vector2(middle - MONSTER_SIZE.width / 2, feet),
    ];

    graphics.fillPoints(points, true);
    graphics.strokePoints(points, true);
  }

  if (look.crown !== null) {
    graphics.fillStyle(look.crown, 1);
    graphics.fillTriangle(
      middle,
      0,
      middle + MONSTER_CROWN.width / 2,
      MONSTER_CROWN.height,
      middle - MONSTER_CROWN.width / 2,
      MONSTER_CROWN.height,
    );
  }
};

/**
 * The life bar, in two pieces: a dark trough and a bright fill.
 *
 * Two textures rather than one per band, because the fill is placed with a
 * left-hand origin and stretched to however many pixels the band is worth
 * (`lifeBarPixels`) — a solid rectangle, so stretching it is exact. The trough
 * stays whatever the fill does, which is what makes an empty bar look like an
 * empty bar rather than like a monster with no bar at all.
 */
const drawBar = (graphics: Phaser.GameObjects.Graphics): void => {
  graphics.fillStyle(PALETTE.monsterLifeEmpty, 1);
  graphics.fillRect(0, 0, MONSTER_BAR.width, MONSTER_BAR.height);
  graphics.lineStyle(EDGE_WIDTH, PALETTE.monsterLifeEdge, 1);
  graphics.strokeRect(0, 0, MONSTER_BAR.width, MONSTER_BAR.height);
};

/**
 * A drop on the floor: a flat lozenge, drawn white so it can be tinted.
 *
 * White is a requirement rather than a colour choice — Phaser's tint multiplies,
 * so a shape baked in anything else could not be tinted *to* the rarity hexes
 * `docs/GAME-DESIGN.md` names, only towards them. What each drop is actually
 * tinted is `ground-art.ts`'s, which reads the palette off core's own table.
 *
 * A diamond rather than a rectangle, for the reason the elite ring is an
 * ellipse: everything lying on this floor is drawn 2:1, so a rectangle would
 * read as standing up.
 */
const drawGroundItem = (graphics: Phaser.GameObjects.Graphics): void => {
  const half = { x: GROUND_ITEM_SIZE.width / 2, y: GROUND_ITEM_SIZE.height / 2 };
  const points = [
    corner(half.x, EDGE_WIDTH),
    corner(GROUND_ITEM_SIZE.width - EDGE_WIDTH, half.y),
    corner(half.x, GROUND_ITEM_SIZE.height - EDGE_WIDTH),
    corner(EDGE_WIDTH, half.y),
  ];

  fillOutlined(graphics, points, PALETTE.groundItem, PALETTE.groundItemEdge);
};

/**
 * The way down: a well sunk into the cell, drawn as nested diamonds getting
 * darker inward.
 *
 * Flat and 2:1 like every other flat thing in this projection, so it reads as a
 * **hole in the floor** rather than a box standing on it — the same argument the
 * elite ring and the drop lozenge make. The nesting is what makes it a descent
 * and not a rug: three rims stepping inward and downward is the cheapest picture
 * of stairs that survives being 44 pixels across.
 *
 * The brightest ring is the outermost, so the mark reads from the outside in —
 * which is how it is seen, since a body standing on the cell covers the middle
 * of it first.
 */
const drawStairs = (graphics: Phaser.GameObjects.Graphics): void => {
  const middle = { x: STAIRS_SIZE.width / 2, y: STAIRS_SIZE.height / 2 };

  for (let step = 0; step < STAIRS_STEPS; step += 1) {
    const shrink = step / STAIRS_STEPS;
    const half = {
      x: (STAIRS_SIZE.width / 2) * (1 - shrink) - EDGE_WIDTH,
      y: (STAIRS_SIZE.height / 2) * (1 - shrink) - EDGE_WIDTH,
    };

    fillOutlined(
      graphics,
      [
        corner(middle.x, middle.y - half.y),
        corner(middle.x + half.x, middle.y),
        corner(middle.x, middle.y + half.y),
        corner(middle.x - half.x, middle.y),
      ],
      step === 0 ? PALETTE.stairs : PALETTE.stairsStep,
      PALETTE.stairsEdge,
    );
  }
};

/**
 * The arrow that says which way the stairs are when they are off screen.
 *
 * Drawn **pointing right**, because `beacon.ts` answers a bearing in degrees
 * clockwise from screen right and Phaser's `setAngle` turns a texture from that
 * same zero — so one assignment aims it and no offset has to be remembered
 * anywhere.
 *
 * A solid chevron rather than a thin arrow: it is drawn over whatever the edge
 * of the view happens to be — rock, floor, a body — and a silhouette with a rim
 * is what stays readable against all three. In the stairwell's own colour,
 * because its whole job is to be recognised as the same mark the moment the
 * stairs themselves come into view.
 */
const drawBeacon = (graphics: Phaser.GameObjects.Graphics): void => {
  const { width, height } = STAIRS_BEACON_SIZE;
  const inset = EDGE_WIDTH;
  const notch = width / 3;

  fillOutlined(
    graphics,
    [
      corner(width - inset, height / 2),
      corner(inset, inset),
      corner(inset + notch, height / 2),
      corner(inset, height - inset),
    ],
    PALETTE.stairs,
    PALETTE.stairsEdge,
  );
};

/**
 * One action mark: what a body is *doing*, as a silhouette of its own.
 *
 * Drawn beside the body rather than into it, which is the whole design
 * (`action-art.ts`): the fill of a body is its archetype, the ring under its
 * feet is its tier, and both have a player's eye and a pixel probe aimed at
 * them. So an action gets a band of its own — the shoulders — and a shape of its
 * own, and colour is what separates two marks rather than what carries the
 * meaning alone.
 *
 * **Every shape here is symmetric about its middle**, because nothing on this
 * wire says which way a body is facing (`TODO.md` #32 keeps facing out of
 * scope), and a mark that leaned one way would be wrong half the time.
 *
 * The dark outline is not decoration: a mark is drawn over a body whose colour
 * could be anything from hot pink to bright green, and a rim is what keeps the
 * silhouette readable against all three archetypes and the floor behind them.
 */
const drawMark = (graphics: Phaser.GameObjects.Graphics, mark: ActionMark): void => {
  const { width, height } = mark.size;
  const middle = { x: width / 2, y: height / 2 };
  const inset = EDGE_WIDTH;

  if (mark.shape === 'slash') {
    // A blow, mid-swing: one long lens straight through the body and out the
    // far side. The widest mark there is, on purpose — a silhouette broken
    // horizontally is what the eye finds first in a crowd of sixty.
    fillOutlined(
      graphics,
      [
        corner(inset, middle.y),
        corner(middle.x, inset),
        corner(width - inset, middle.y),
        corner(middle.x, height - inset),
      ],
      mark.colour,
      PALETTE.monsterEdge,
    );

    return;
  }

  if (mark.shape === 'gather') {
    // A blow being gathered: the slash's lens, halved and turned inward. Each
    // wedge is a triangle whose point reaches the middle of the body — the two
    // ends of the swing folded back into it — where the strike is one bar whose
    // points come out the far sides. Same silhouette family, opposite reading:
    // closing, then thrown.
    //
    // It stops at the edges of the frame, and the frame is exactly a body wide
    // (`ACTION_GATHER`), so the outline is never broken by a wind-up and always
    // is by the swing. Air down the middle so it can never read as the one
    // continuous bar a strike draws, whatever the two are scaled to.
    const gap = width / 8;

    for (const direction of [-1, 1] as const) {
      const outer = direction < 0 ? inset : width - inset;
      const point = middle.x + direction * gap;

      fillOutlined(
        graphics,
        [corner(outer, inset), corner(point, middle.y), corner(outer, height - inset)],
        mark.colour,
        PALETTE.monsterEdge,
      );
    }

    return;
  }

  if (mark.shape === 'motes') {
    // A cast: two motes with clear air between them, so it cannot be mistaken
    // for the one continuous bar a swing draws.
    const radius = height / 2;

    for (const x of [radius, width - radius]) {
      fillOutlined(
        graphics,
        [
          corner(x - radius + inset, middle.y),
          corner(x, inset),
          corner(x + radius - inset, middle.y),
          corner(x, height - inset),
        ],
        mark.colour,
        PALETTE.monsterEdge,
      );
    }

    return;
  }

  if (mark.shape === 'burst') {
    // A blow landing *on* this body: spikes out of the middle, which is the one
    // shape here that is neither a bar nor a pair.
    const points: Phaser.Math.Vector2[] = [];
    const radius = { x: middle.x - inset, y: middle.y - inset };

    for (let step = 0; step < 8; step += 1) {
      const angle = (step * Math.PI) / 4;
      const reach = step % 2 === 0 ? 1 : 0.38;

      points.push(corner(middle.x + Math.cos(angle) * radius.x * reach, middle.y + Math.sin(angle) * radius.y * reach));
    }

    fillOutlined(graphics, points, mark.colour, PALETTE.monsterEdge);

    return;
  }

  if (mark.shape === 'guard') {
    // A guard held up: the one **hollow** mark, so the body's own colour shows
    // through the middle of it. Nothing is being taken away by a block.
    const radius = middle.x - 3;

    for (const [thickness, colour] of [
      [6, PALETTE.monsterEdge],
      [3, mark.colour],
    ] as const) {
      graphics.lineStyle(thickness, colour, 1);
      graphics.beginPath();
      graphics.arc(middle.x, height - inset, radius, Math.PI, Math.PI * 2);
      graphics.strokePath();
    }

    return;
  }

  if (mark.shape === 'dust') {
    // Feet moving, and nothing more. No outline anywhere: on the tick a floor of
    // sixty closes on a player, every one of them draws this, and it must not
    // compete with the one body that is swinging.
    const dash = { width: (width - height) / 2, height: height / 2 };

    graphics.fillStyle(mark.colour, 1);
    graphics.fillRect(0, dash.height / 2, dash.width, dash.height);
    graphics.fillRect(width - dash.width, dash.height / 2, dash.width, dash.height);

    return;
  }

  // Blood on the floor under a corpse. Fill only and 2:1, like the elite ring
  // and every other flat thing in this projection, so it reads as lying on the
  // ground rather than standing on it.
  graphics.fillStyle(mark.colour, 1);
  graphics.fillEllipse(middle.x, middle.y, width, height);
};

const bakeTileset = (scene: Phaser.Scene, graphics: Phaser.GameObjects.Graphics, key: string): void => {
  graphics.generateTexture(key, TILESET_IMAGE.width, TILESET_IMAGE.height);

  // Nearest-neighbour: tiles are drawn at 1:1, so interpolation can only smear a
  // frame's gutter into its edge and buys nothing in return.
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
};

/**
 * Names the frames of the sprite copy, by the very tile index the tilemap uses,
 * so a rock is `ROCK_TILE` whichever of the two is asking for it.
 */
const addTileFrames = (texture: Phaser.Textures.Texture): void => {
  for (const [column, tile] of [FLOOR_TILE, ROCK_TILE].entries()) {
    const origin = frameOrigin(column);

    texture.add(tile, 0, origin.x, origin.y, TILE_FRAME.width, TILE_FRAME.height);
  }
};

/**
 * Every monster texture there is: one per archetype and standing.
 *
 * Twelve of them, baked once and shared by however many bodies the room spawns —
 * the count answers to the *vocabulary*, never to the population, so a floor of
 * sixty costs the same twelve textures a floor of one does.
 */
const MONSTER_LOOKS: readonly MonsterLook[] = MONSTER_ARCHETYPES.flatMap((archetype: MonsterArchetype) =>
  MONSTER_BADGES.map((badge) => monsterLook(archetype, badge)),
);

/**
 * Bakes the placeholder textures into the scene's texture manager. Idempotent,
 * so restarting a scene does not fight the texture cache.
 */
export const generatePlaceholderArt = (scene: Phaser.Scene): void => {
  const baked = [
    CAVE_TILESET,
    TILE_SPRITES,
    ENTITY_TEXTURE,
    ENTITY_OTHER_TEXTURE,
    MONSTER_BAR_TEXTURE,
    MONSTER_BAR_FILL_TEXTURE,
    GROUND_ITEM_TEXTURE,
    STAIRS_TEXTURE,
    STAIRS_BEACON_TEXTURE,
    ...MONSTER_LOOKS.map((look) => look.key),
    ...ACTION_MARKS.map((mark) => mark.key),
  ];
  if (baked.every((key) => scene.textures.exists(key))) return;

  const graphics = scene.make.graphics({}, false);

  drawTileset(graphics);
  bakeTileset(scene, graphics, CAVE_TILESET);
  bakeTileset(scene, graphics, TILE_SPRITES);
  addTileFrames(scene.textures.get(TILE_SPRITES));

  graphics.clear();
  drawEntity(graphics, PALETTE.entity, PALETTE.entityEdge);
  graphics.generateTexture(ENTITY_TEXTURE, ENTITY_SIZE.width, ENTITY_SIZE.height);

  graphics.clear();
  drawEntity(graphics, PALETTE.entityOther, PALETTE.entityOtherEdge);
  graphics.generateTexture(ENTITY_OTHER_TEXTURE, ENTITY_SIZE.width, ENTITY_SIZE.height);

  for (const look of MONSTER_LOOKS) {
    graphics.clear();
    drawMonster(graphics, look);
    graphics.generateTexture(look.key, MONSTER_FRAME.width, MONSTER_FRAME.height);
  }

  graphics.clear();
  drawBar(graphics);
  graphics.generateTexture(MONSTER_BAR_TEXTURE, MONSTER_BAR.width, MONSTER_BAR.height);

  graphics.clear();
  graphics.fillStyle(PALETTE.monsterLife, 1);
  graphics.fillRect(0, 0, MONSTER_BAR_FILL.width, MONSTER_BAR_FILL.height);
  graphics.generateTexture(MONSTER_BAR_FILL_TEXTURE, MONSTER_BAR_FILL.width, MONSTER_BAR_FILL.height);
  // Nearest-neighbour, because this one is the only texture that is *stretched*:
  // a bar is drawn at the width its life band is worth. The rectangle is one
  // flat colour, so nearest keeps both ends of it crisp and interpolation could
  // only smear the last column into whatever is behind it.
  scene.textures.get(MONSTER_BAR_FILL_TEXTURE).setFilter(Phaser.Textures.FilterMode.NEAREST);

  graphics.clear();
  drawGroundItem(graphics);
  graphics.generateTexture(GROUND_ITEM_TEXTURE, GROUND_ITEM_SIZE.width, GROUND_ITEM_SIZE.height);

  // One well and one arrow, whatever the floor is: a level has exactly one way
  // down, and the beacon that points at it is the same picture turned.
  graphics.clear();
  drawStairs(graphics);
  graphics.generateTexture(STAIRS_TEXTURE, STAIRS_SIZE.width, STAIRS_SIZE.height);

  graphics.clear();
  drawBeacon(graphics);
  graphics.generateTexture(STAIRS_BEACON_TEXTURE, STAIRS_BEACON_SIZE.width, STAIRS_BEACON_SIZE.height);

  // One texture per action that has a mark — seven, and the count answers to the
  // wire's vocabulary rather than to the population, exactly as the twelve
  // monster bodies above do. A floor of sixty in a fight costs no more textures
  // than a floor of one.
  for (const mark of ACTION_MARKS) {
    graphics.clear();
    drawMark(graphics, mark);
    graphics.generateTexture(mark.key, mark.size.width, mark.size.height);
  }

  graphics.destroy();
};
